import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (err) {
        setTokenValid(false);
        setError(err.response?.data?.detail || 'Invalid or expired reset link');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '1rem'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e9ecef',
            borderTopColor: '#4A90E2',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#666' }}>Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(1.5rem, 5vw, 2.5rem)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '450px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#f8d7da',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <AlertTriangle size={32} style={{ color: '#dc3545' }} />
          </div>
          
          <h2 style={{ fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', color: '#333', marginBottom: '1rem' }}>
            Invalid Reset Link
          </h2>
          
          <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', color: '#666', marginBottom: '2rem', lineHeight: '1.6' }}>
            {error || 'This password reset link is invalid or has expired. Please request a new one.'}
          </p>
          
          <Link 
            to="/forgot-password"
            style={{
              display: 'inline-block',
              padding: 'clamp(0.75rem, 3vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '600',
              color: 'white',
              backgroundColor: '#4A90E2',
              textDecoration: 'none',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}
          >
            Request New Link
          </Link>
          
          <div>
            <Link 
              to="/login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#4A90E2',
                textDecoration: 'none',
                fontSize: 'clamp(0.9rem, 3vw, 1rem)',
                fontWeight: '500'
              }}
            >
              <ArrowLeft size={18} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: '1rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 'clamp(1.5rem, 5vw, 2.5rem)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '450px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#d4edda',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <CheckCircle size={32} style={{ color: '#28a745' }} />
          </div>
          
          <h2 style={{ fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', color: '#333', marginBottom: '1rem' }}>
            Password Reset!
          </h2>
          
          <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1.1rem)', color: '#666', marginBottom: '2rem', lineHeight: '1.6' }}>
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          
          <button
            onClick={() => navigate('/login')}
            style={{
              width: '100%',
              padding: 'clamp(0.85rem, 3vw, 1rem)',
              fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
              fontWeight: '600',
              color: 'white',
              backgroundColor: '#4A90E2',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: 'clamp(1.5rem, 5vw, 2.5rem)',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        maxWidth: '450px',
        width: '100%'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)', color: '#333', marginBottom: '0.5rem' }}>
            Reset Password
          </h1>
          <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1rem)', color: '#666' }}>
            Enter a new password for <strong>{email}</strong>
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              <Lock size={18} />
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={6}
              style={{
                width: '100%',
                padding: 'clamp(0.75rem, 3vw, 1rem)',
                fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
                border: '2px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4A90E2'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '600',
              color: '#333',
              marginBottom: '0.5rem'
            }}>
              <Lock size={18} />
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
              style={{
                width: '100%',
                padding: 'clamp(0.75rem, 3vw, 1rem)',
                fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
                border: '2px solid #ddd',
                borderRadius: '8px',
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4A90E2'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: 'clamp(0.85rem, 3vw, 1rem)',
              fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
              fontWeight: '600',
              color: 'white',
              backgroundColor: loading ? '#7fb3e8' : '#4A90E2',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              marginBottom: '1.5rem'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#357ABD')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#4A90E2')}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div style={{ textAlign: 'center' }}>
          <Link 
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#4A90E2',
              textDecoration: 'none',
              fontSize: 'clamp(0.9rem, 3vw, 1rem)',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={18} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
